import ListViewModel from "viewModels/listViewModel";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Router } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation/dist";
import InternalReportsRepository from "repositories/internalReportsRepository";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import InternalReportResponse from "entities/internalReportResponse";
import { allowToDownload } from "../rights";
import { Modules } from "config";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import type InternalReportDetailViewModel from "../internalReportDetailViewModel";
import InternalBusinessReportRequest, {
  InternalReportRequestBusinessValidationRules,
} from "entities/InternalBusinessReportRequest";
import { isValid } from "date-fns";
import { endOfCzechDay, startOfCzechDay } from "utils/dateUtils";

const navigationName = "business-reports";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class BusinessReportsListViewModel extends ListViewModel<
  InternalReportResponse,
  InternalReportDetailViewModel,
  ScreenBase,
  InternalBusinessReportRequest
> {
  readonly modelName = "internalReportResponse";
  @observable items: InternalReportResponse[] = [];
  @observable filterItem: InternalBusinessReportRequest;
  @observable fileResponse: any = undefined;
  @observable invalidValidSince = false;
  @observable invalidValidUntil = false;

  readonly idProperty = "id";

  constructor(
    protected detailViewModelFactory: ReturnType<typeof InternalReportDetailViewModel.Factory>,
    public repository: InternalReportsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService
  ) {
    super();
    this.navigationName = navigationName;
    this.filterItem = new InternalBusinessReportRequest();
    attachAutomaticValidator(this.filterItem, InternalReportRequestBusinessValidationRules);
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @action.bound
  async onDownload(item: InternalReportResponse) {
    await this.download(item);
  }

  @action.bound
  validateDate(date: any) {
    if (date === undefined || date?.length === 0) {
      return true;
    }
    if (date instanceof Date) {
      return isValid(new Date(date));
    }
    return isValid(date);
  }

  @action.bound
  setInputValidSince(input: any) {
    this.invalidValidSince = input;
  }

  @action.bound
  setInputValidUntil(input: any) {
    this.invalidValidUntil = input;
  }

  @action.bound
  onResetClick() {
    this.filterItem = new InternalBusinessReportRequest();
    attachAutomaticValidator(this.filterItem, InternalReportRequestBusinessValidationRules);
  }

  @action.bound
  async fetchData() {
    const { offset, limit } = this.pagingFilter;
    const filter = { ...this.filterItem, offset, limit };
    const reports = await this.repository.postBusinessReports(filter);

    reports.items = reports.items.sort((a, b) => {
      if (!a.processedAt || !b.processedAt) {
        return 1;
      }
      return b.processedAt.getTime() - a.processedAt.getTime();
    });

    return reports;
  }

  @action.bound
  filterData() {
    if (isValid(this.filterItem.toDate)) {
      this.filterItem.toDate = endOfCzechDay(this.filterItem.toDate);
    }
    if (isValid(this.filterItem.fromDate)) {
      this.filterItem.fromDate = startOfCzechDay(this.filterItem.fromDate);
    }
    if (!this.invalidValidSince && !this.invalidValidUntil && validate(this.filterItem)) {
      this.loadData();
    }
  }

  async download(item: InternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.fileName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `internalReports.businessReports.${code}`, size);
  }
}
